import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { CreditCard, Plus, FileDown, Pencil, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { parseApiError } from '../lib/parseApiError';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const oggiIso = () => new Date().toISOString().slice(0, 10);
const fmtData = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '-');
const STATI = { attivo: 'bg-emerald-100 text-emerald-700', sospeso: 'bg-amber-100 text-amber-700', scaduto: 'bg-red-100 text-red-700' };

const VUOTO = {
  cliente_id: '', data_contratto: oggiIso(), canone_mensile: '', data_scadenza_addebito: '',
  data_emissione_fattura: '', banca: '', iban: '', bic_swift: '', id_creditore_sepa: '', note: '', stato: 'attivo',
};

export default function ContrattiGestionaliPage() {
  const { isAdmin } = useAuth();
  const [contratti, setContratti] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(VUOTO);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [resC, resCli] = await Promise.all([
        axios.get(`${API}/contratti-gestionali`),
        axios.get(`${API}/clienti`),
      ]);
      setContratti(resC.data);
      setClienti([...resCli.data].sort((a, b) => (a.ragione_sociale || '').localeCompare(b.ragione_sociale || '')));
    } catch (e) {
      toast.error('Errore caricamento contratti gestionali');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const apriNuovo = () => { setEditing(null); setForm({ ...VUOTO, data_contratto: oggiIso() }); setDialogOpen(true); };
  const apriModifica = (c) => {
    setEditing(c);
    setForm({
      cliente_id: c.cliente_id, data_contratto: c.data_contratto || '', canone_mensile: c.canone_mensile || '',
      data_scadenza_addebito: c.data_scadenza_addebito || '', data_emissione_fattura: c.data_emissione_fattura || '',
      banca: c.banca || '', iban: c.iban || '', bic_swift: c.bic_swift || '',
      id_creditore_sepa: c.id_creditore_sepa || '', note: c.note || '', stato: c.stato || 'attivo',
    });
    setDialogOpen(true);
  };

  const salva = async () => {
    if (!form.cliente_id) { toast.error('Seleziona il cliente'); return; }
    setSaving(true);
    try {
      const payload = { ...form, canone_mensile: parseFloat(form.canone_mensile) || 0 };
      if (editing) {
        await axios.put(`${API}/contratti-gestionali/${editing.id}`, payload);
        toast.success('Contratto gestionale aggiornato');
      } else {
        const res = await axios.post(`${API}/contratti-gestionali`, payload);
        toast.success(`Contratto creato — mandato n. ${res.data.numero_mandato}`);
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) { toast.error(parseApiError(e, 'Errore salvataggio')); }
    finally { setSaving(false); }
  };

  const elimina = async (c) => {
    if (!window.confirm(`Eliminare il contratto gestionale ${c.numero_mandato} di ${c.cliente_nome}?`)) return;
    try {
      await axios.delete(`${API}/contratti-gestionali/${c.id}`);
      toast.success('Contratto eliminato');
      fetchData();
    } catch (e) { toast.error(parseApiError(e, 'Errore eliminazione')); }
  };

  const scaricaPdf = async (c) => {
    try {
      const res = await axios.get(`${API}/contratti-gestionali/${c.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Mandato_${c.numero_mandato}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Mandato SDD scaricato');
    } catch (e) { toast.error('Errore generazione PDF'); }
  };

  const inputCls = 'rounded-none';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contratti-gestionali-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tight flex items-center gap-3">
            <CreditCard className="h-8 w-8 text-violet-500" />
            Contratti Gestionali
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Mandati SDD per il gestionale Velta One (V1) — canone mensile con addebito ricorrente</p>
        </div>
        <Button onClick={apriNuovo} className="rounded-none" data-testid="nuovo-ctr-gest-btn">
          <Plus className="h-4 w-4 mr-2" />Nuovo contratto
        </Button>
      </div>

      <Card className="rounded-md">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>N. Mandato</th>
                  <th>Cliente</th>
                  <th>Data contratto</th>
                  <th>Canone mensile</th>
                  <th className="hidden md:table-cell">Scadenza addebito</th>
                  <th className="hidden md:table-cell">Emissione fattura</th>
                  <th>Stato</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
                ) : contratti.length === 0 ? (
                  <tr><td colSpan={8} className="text-center text-muted-foreground py-10">Nessun contratto gestionale. Crea il primo con "Nuovo contratto".</td></tr>
                ) : (
                  contratti.map((c) => (
                    <tr key={c.id} className="hover:bg-muted transition-colors" data-testid={`ctr-gest-row-${c.id}`}>
                      <td className="font-mono text-sm font-semibold">{c.numero_mandato}</td>
                      <td className="text-sm max-w-[220px] truncate" title={c.cliente_nome}>{c.cliente_nome}</td>
                      <td className="font-mono text-xs">{fmtData(c.data_contratto)}</td>
                      <td className="font-mono text-sm text-emerald-600 font-medium">€ {(c.canone_mensile || 0).toFixed(2)}</td>
                      <td className="hidden md:table-cell font-mono text-xs">{fmtData(c.data_scadenza_addebito)}</td>
                      <td className="hidden md:table-cell font-mono text-xs">{fmtData(c.data_emissione_fattura)}</td>
                      <td>
                        <Badge className={`text-[10px] uppercase ${STATI[c.stato] || STATI.attivo}`} data-testid={`ctr-gest-stato-${c.id}`}>
                          {c.stato || 'attivo'}
                        </Badge>
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-blue-600" onClick={() => scaricaPdf(c)} title="Scarica PDF Mandato SDD" data-testid={`ctr-gest-pdf-${c.id}`}>
                            <FileDown className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md" onClick={() => apriModifica(c)} title="Modifica" data-testid={`ctr-gest-edit-${c.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {isAdmin() && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-red-600" onClick={() => elimina(c)} title="Elimina" data-testid={`ctr-gest-del-${c.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl rounded-none max-h-[90vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="font-mono uppercase tracking-wider">
              {editing ? `Modifica ${editing.numero_mandato}` : 'Nuovo Contratto Gestionale'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto pr-2 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs font-mono uppercase">Cliente *</Label>
                <select value={form.cliente_id} onChange={(e) => set('cliente_id', e.target.value)} data-testid="ctr-gest-cliente"
                  className="flex h-9 w-full rounded-none border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                  <option value="">— Seleziona cliente —</option>
                  {clienti.map((cl) => <option key={cl.id} value={cl.id}>{cl.ragione_sociale}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Numero mandato</Label>
                <Input value={editing ? editing.numero_mandato : 'Generato automaticamente'} disabled className={inputCls} data-testid="ctr-gest-mandato" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Data contratto</Label>
                <Input type="date" value={form.data_contratto} onChange={(e) => set('data_contratto', e.target.value)} className={inputCls} data-testid="ctr-gest-data" />
                <p className="text-[10px] text-muted-foreground mt-0.5">Modificabile — proposta la data di oggi</p>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Canone mensile (€) *</Label>
                <Input type="number" step="0.01" min="0" value={form.canone_mensile} onChange={(e) => set('canone_mensile', e.target.value)} placeholder="es. 49.00" className={inputCls} data-testid="ctr-gest-canone" />
                {parseFloat(form.canone_mensile) > 0 && (
                  <p className="text-[11px] text-emerald-600 font-medium mt-0.5" data-testid="ctr-gest-canone-iva">
                    € {parseFloat(form.canone_mensile).toFixed(2)} + IVA 22% = <strong>€ {(Math.round(parseFloat(form.canone_mensile) * 122) / 100).toFixed(2)}</strong> totale
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Stato</Label>
                <select value={form.stato} onChange={(e) => set('stato', e.target.value)} data-testid="ctr-gest-stato-sel"
                  className="flex h-9 w-full rounded-none border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                  <option value="attivo">Attivo</option>
                  <option value="sospeso">Sospeso</option>
                  <option value="scaduto">Scaduto</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Data scadenza addebito mensile</Label>
                <Input type="date" value={form.data_scadenza_addebito} onChange={(e) => set('data_scadenza_addebito', e.target.value)} className={inputCls} data-testid="ctr-gest-scad-addebito" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Data emissione fattura mensile</Label>
                <Input type="date" value={form.data_emissione_fattura} onChange={(e) => set('data_emissione_fattura', e.target.value)} className={inputCls} data-testid="ctr-gest-emiss-fattura" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Banca del cliente</Label>
                <Input value={form.banca} onChange={(e) => set('banca', e.target.value)} placeholder="es. Intesa Sanpaolo" className={inputCls} data-testid="ctr-gest-banca" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">IBAN</Label>
                <Input value={form.iban} onChange={(e) => set('iban', e.target.value.toUpperCase())} placeholder="IT00 X000 0000 0000 0000 0000 000" className={inputCls} data-testid="ctr-gest-iban" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">BIC/SWIFT</Label>
                <Input value={form.bic_swift} onChange={(e) => set('bic_swift', e.target.value.toUpperCase())} placeholder="es. BCITITMM" className={inputCls} data-testid="ctr-gest-bic" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Identificativo Creditore SEPA</Label>
                <Input value={form.id_creditore_sepa} onChange={(e) => set('id_creditore_sepa', e.target.value)} placeholder="(per ora vuoto)" className={inputCls} data-testid="ctr-gest-sepa" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-mono uppercase">Note interne</Label>
                <Textarea value={form.note} onChange={(e) => set('note', e.target.value)} className="rounded-none min-h-[70px]" data-testid="ctr-gest-note" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-border shrink-0">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none" data-testid="ctr-gest-annulla">Annulla</Button>
            <Button onClick={salva} disabled={saving} className="rounded-none" data-testid="ctr-gest-salva">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editing ? 'Aggiorna' : 'Crea contratto'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
